import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/auth/authStore';
import { hasAccess } from '@/auth/roles';
import { cn } from '@/lib/utils';
import { useNavCounts } from '@/features/ops/hooks';
import { LiveIndicator } from '@/features/ops/LiveIndicator';
import { CurrentTenantTestModeBadge } from '@/features/tenants/TestModeBadge';
import { navSections } from '@/routes/nav';
import { CommandPalette } from './CommandPalette';
import { TenantSelector } from './TenantSelector';
import { UserMenu } from './UserMenu';

function Sidebar() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const counts = useNavCounts();

  return (
    <aside className="hidden w-52 shrink-0 flex-col border-r bg-sidebar md:flex">
      <div className="flex h-12 items-center gap-2 px-4">
        <img src="/favicon.svg" alt="" className="size-5" />
        <span className="text-sm font-semibold">{t('app.name')}</span>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 py-2" aria-label="Main">
        {navSections.map((section) => {
          const items = section.items.filter((i) => !i.access || (user && hasAccess(user.roles, user.caps, i.access)));
          if (items.length === 0) return null;
          return (
            <div key={section.labelKey} className="mb-3">
              <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t(section.labelKey)}</div>
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground',
                      isActive && 'bg-muted font-medium text-foreground',
                    )
                  }
                >
                  <item.icon className="size-4" />
                  {t(item.labelKey)}
                  {item.badge && counts[item.badge] > 0 && (
                    <span className="ml-auto rounded-full bg-accent px-1.5 text-[10px] font-medium tabular-nums text-accent-foreground" data-testid={`nav-badge-${item.badge}`}>
                      {counts[item.badge] > 99 ? '99+' : counts[item.badge]}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-3 border-b px-4">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}
            className="flex h-8 w-full max-w-64 items-center gap-2 rounded-md border bg-background px-2 text-xs text-muted-foreground hover:bg-muted"
          >
            <Search className="size-3.5" />
            <span className="flex-1 text-left">{t('command.hint')}</span>
            <kbd className="rounded border px-1 font-mono text-[10px]">Ctrl K</kbd>
          </button>
          <div className="ml-auto flex items-center gap-2">
            <CurrentTenantTestModeBadge />
            <LiveIndicator />
            <TenantSelector />
            <UserMenu />
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}
